import {FC, useMemo} from "react";
import {Box, BoxProps, LinearProgress} from "@mui/material";
import {AdminHeader} from "@/pages/ManagerView/AdminHeader";
import {makeStyles} from "tss-react/mui";
import {DataGridPro, GridColDef, GRID_CHECKBOX_SELECTION_COL_DEF, GridToolbar} from "@mui/x-data-grid-pro";
import {useNavigate} from "react-router-dom";
import {convertSecondsToHHMMSS} from "@/utils";
import {useGetI18nLocales, CourseLessonDto} from "@/app2/shared/data";
import type {CoursesControllerListLessonsParams} from "@/app2/shared/data/gen";
import {AdminTableColumnsPanel} from "@/components/common/table/AdminTableColumnsPanel";
import useCustomDataGrid from "@/app2/shared/hooks/useCustomDataGrid";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {AdminTableLanguagesFilterSelect} from "@/components/common/table/AdminTableLanguagesFilterSelect";
import {useCourseLessonsListGrid} from "../../hooks";
import {formatDateForDisplay} from "@/app2/shared/utils/dateManipulation";

const useStyles = makeStyles()((theme) => ({
	contentBox: {
		marginTop: theme.spacing(3),
		padding: theme.spacing(2.5),
		backgroundColor: theme.palette.background.paper,
		borderWidth: theme.borderWidth.small,
		borderColor: theme.palette.grey[100],
		borderStyle: "solid",
		borderRadius: theme.borderRadius.small
	},
	dataGrid: {
		padding: 0,
		border: 0,
		"& th": {
			fontSize: "11px",
			fontWeight: 700,
			textTransform: "uppercase"
		}
	}
}));

interface ICustomProps {
	courseId: number | undefined;
	course?: {
		title?: string;
	};
}

export type IProps = Omit<BoxProps, "children"> & ICustomProps;

export const LessonsTab: FC<IProps> = ({courseId, course, ...baseProps}) => {
	const {classes} = useStyles();
	const navigate = useNavigate();

	const {
		api: {assignFilters}
	} = useCustomDataGrid<CourseLessonDto>({});

	const {
		rows,
		rowCount,
		isLoading,
		isFetched,
		page,
		pageSize,
		onPageChange,
		onPageSizeChange,
		onFilterChange,
		onSortModelChange,
		defaultInitialState
	} = useCourseLessonsListGrid({
		courseId
	});

	const data = useMemo(
		() => (isFetched && !isLoading ? rows : []),
		[isFetched, isLoading, rows]
	);

	const {data: localesData, isLoading: localesLoading} = useGetI18nLocales();

	const locales = localesData?.data || [];

	const columns: GridColDef<CourseLessonDto>[] = useMemo<GridColDef<CourseLessonDto>[]>(
		() => [
			{...GRID_CHECKBOX_SELECTION_COL_DEF, type: "string", hideable: false},
			{
				field: "title",
				type: "string",
				flex: 1,
				headerName: "Title",
				minWidth: 240
			},
			{
				field: "videoDuration",
				type: "number",
				flex: 1,
				headerName: "Duration",
				valueGetter: (params: any) =>
					params.row.videoDuration ? convertSecondsToHHMMSS(params.row.videoDuration) : ""
			},
			{
				field: "updatedAt",
				type: "date",
				flex: 1,
				headerName: "Last Modified",
				valueGetter: (params: any) => (params.row.updatedAt ? new Date(params.row.updatedAt) : null),
				valueFormatter: (params: any) =>
					formatDateForDisplay(params.value as Date | null)
			},
			{
				field: "languages",
				type: "string",
				filterable: true,
				sortable: false,
				flex: 1,
				headerName: "Supported Languages",
				valueGetter: (params: any) => {
					const languages = params.row.languages || [];
					return languages
						.map((code: string) => locales.find((l: any) => l.code === code)?.name || code)
						.join(", ");
				},
				filterOperators: [
					{
						label: "contains",
						value: "co",
						InputComponent: AdminTableLanguagesFilterSelect,
						getApplyFilterFn: () => null
					}
				]
			}
		],
		[locales]
	);

	const handleRowClick = (params: any): void => {
		navigate(params.id.toString());
	};

	if (!courseId) {
		return null;
	}

	return (
		<Box {...baseProps}>
			<Box className={classes.contentBox}>
				<AdminHeader title={`${course?.title || "Course"} Lessons`} />
				{localesLoading ? (
					<LinearProgress />
				) : (
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DataGridPro
							className={classes.dataGrid}
							loading={isLoading}
							sortingMode="server"
							page={page}
							pageSize={pageSize}
							onPageSizeChange={onPageSizeChange}
							onPageChange={onPageChange}
							checkboxSelection
							keepNonExistentRowsSelected
							paginationMode="server"
							pagination
							rowCount={rowCount}
							onSortModelChange={onSortModelChange}
							rows={data}
							columns={columns.map((e) => assignFilters(e as any))}
							onRowClick={handleRowClick}
							filterMode="server"
							
							onFilterModelChange={onFilterChange}
							components={{Toolbar: GridToolbar, ColumnsPanel: AdminTableColumnsPanel}}
							rowsPerPageOptions={[25, 50, 100, 200]}
							initialState={defaultInitialState}
							getRowId={(row) => row.id}
							disableSelectionOnClick
							autoHeight
							sx={{
								"& .MuiDataGrid-row": {
									cursor: "pointer"
								}
							}}
						/>
					</LocalizationProvider>
				)}
				{isLoading && <LinearProgress />}
			</Box>
		</Box>
	);
};
